/**
 * DepositModal — wpłata na cel. Manual lub z portfela.
 */
import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Icons } from '../Icons';
import SegmentedControl from '../ui/SegmentedControl';
import { WalletPicker } from '../transactions/WalletPicker';
import { type Goal, goalSaved } from '../../features/goals/goalsStore';
import { depositToGoal } from '../../features/goals/api';
import { useAuthStore } from '../../features/auth/authStore';
import { useWalletsStore } from '../../features/wallets/walletsStore';
import { parseAmountInput, haptic, formatCurrency } from '../../lib/utils';
import { showToast } from '../ui/Toast';

interface Props {
  goal: Goal;
  onClose: () => void;
}

export function DepositModal({ goal, onClose }: Props) {
  const wallets = useWalletsStore((s) => s.wallets);
  const family = useAuthStore((s) => s.family);
  const user = useAuthStore((s) => s.user);
  const personal = wallets.filter((w) => w.type !== 'shared');

  const [type, setType] = useState<'manual' | 'wallet'>('manual');
  const [amount, setAmount] = useState('');
  const [walletId, setWalletId] = useState(personal[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const grosze = parseAmountInput(amount);
    if (!grosze) {
      setError('Podaj kwotę');
      return;
    }
    if (type === 'wallet' && !walletId) {
      setError('Wybierz portfel');
      return;
    }
    setSubmitting(true);
    try {
      await depositToGoal({
        goalId: goal.id,
        goalName: goal.name,
        amount: grosze,
        type,
        walletId: type === 'wallet' ? walletId : null,
        familyId: family?.id,
        userId: user?.uid,
      });
      haptic('success');
      showToast(
        type === 'wallet'
          ? `Wpłacono ${formatCurrency(grosze)} (saldo portfela maleje)`
          : `Wpłacono ${formatCurrency(grosze)} na cel`,
        'success',
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się wpłacić');
      haptic('error');
      setSubmitting(false);
    }
  };

  const remaining = Math.max(0, goal.target - goalSaved(goal));

  return (
    <Modal
      onClose={onClose}
      size="md"
      status="success"
      icon={
        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/15 ring-8 ring-emerald-500/20 dark:ring-emerald-400/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
          <Icons.PiggyBank size={28} strokeWidth={2.25} />
        </div>
      }
      title={`Wpłata na ${goal.name}`}
      subtitle={`Pozostało ${formatCurrency(remaining)}`}
      footer={
        <div className="flex gap-2">
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50">Anuluj</button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className="flex-[1.5] px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-card transition disabled:opacity-50">{submitting ? 'Zapisuję...' : 'Wpłać'}</button>
        </div>
      }
    >
      <div className="space-y-4">
        <SegmentedControl<'manual' | 'wallet'>
          options={[
            { key: 'manual', label: 'Ręcznie' },
            { key: 'wallet', label: 'Z portfela' },
          ]}
          value={type}
          onChange={setType}
          fullWidth
        />

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Kwota</label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="w-full text-2xl font-bold tabular-nums px-4 py-3 pr-14 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-base font-semibold text-slate-400">zł</span>
          </div>
        </div>

        {type === 'wallet' && (
          <WalletPicker
            wallets={wallets}
            value={walletId}
            onChange={setWalletId}
            excludeShared
            label="Z portfela"
          />
        )}

        {error && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-sm text-rose-700 dark:text-rose-300">{error}</div>
        )}
      </div>
    </Modal>
  );
}
